import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { useCreateContract, useUploadContractDocument } from '@/lib/hooks/contract'
import { useFetchIdTenants } from '@/lib/hooks/tenant'

export default function CriarContratoForm() {
  const [showForm, setShowForm] = useState(true)
  const [contractName, setContractName] = useState('')
  const [contractType, setContractType] = useState('')
  const [selectedTenantCNPJ, setSelectedTenantCNPJ] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [file, setFile] = useState<File | null>(null)

  const { mutate: createContract } = useCreateContract()
  const { mutate: uploadDocument } = useUploadContractDocument()

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const { data: tenants = [], isLoading: loadingTenants, error: tenantError } = useFetchIdTenants()

  const tenantOptions = tenants.map((tenant: { razao_social: any; cnpj: any }) => ({
    razaoSocial: tenant.razao_social,
    cnpj: tenant.cnpj
  }))

  const handleCreateContract = () => {
    if (!selectedTenantCNPJ) {
      alert('Por favor, selecione uma Empresa.')
      return
    }

    createContract(
      {
        createContractDto: {
          nameContrato: contractName,
          typeContrato: contractType,
          id_tenant: selectedTenantCNPJ
        }
      },
      {
        onSuccess: () => {
          console.log('Contrato criado com sucesso.')
          setShowForm(false) // Move to the document upload step
        },
        onError: error => {
          console.error('Erro ao criar contrato', error)
        }
      }
    )
  }

  const handleCreateAndFinalize = () => {
    if (file) {
      uploadDocument(
        { id_tenant: selectedTenantCNPJ, typeContrato: contractType, file },
        {
          onSuccess: () => {
            console.log('Arquivo enviado com sucesso.')
            setShowModal(true) // Open success dialog
          },
          onError: (error: any) => {
            console.error('Erro ao fazer upload do documento', error)
          }
        }
      )
    } else {
      alert('Por favor, selecione um arquivo antes de finalizar.')
    }
  }

  if (loadingTenants) return <p>Carregando Tenant...</p>
  if (tenantError) return <p>Erro ao carregar Tenant.</p>

  if (showForm) {
    return (
      <Card className="w-full max-w-md bg-background">
        <CardHeader>
          <CardTitle>Novo Contrato</CardTitle>
          <CardDescription>Preencha os campos abaixo para criar um novo contrato.</CardDescription>
        </CardHeader>
        <form>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Contrato</Label>
              <Input
                id="name"
                placeholder="Digite o nome do contrato"
                value={contractName}
                onChange={e => setContractName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Tipo de Contrato</Label>
              <Select value={contractType} onValueChange={setContractType} required>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PARCERIA_COMERCIAL_CEO_GESTOR">PARCERIA COMERCIAL CEO GESTOR</SelectItem>
                  <SelectItem value="PARCERIA_COMERCIAL_CEO_COMERCIAL">PARCERIA COMERCIAL CEO COMERCIAL</SelectItem>
                  <SelectItem value="PARCERIA_COMERCIAL_COMERCIAL">PARCERIA COMERCIAL COMERCIAL</SelectItem>
                  <SelectItem value="PRINCIPAL">PRINCIPAL</SelectItem>
                  <SelectItem value="EXCLUSAO_DE_ICMS_E_ST">EXCLUSÃO DE ICMS E ST</SelectItem>
                  <SelectItem value="EQUIPARACAO_HOSPITALAR">EQUIPARAÇÃO HOSPITALAR</SelectItem>
                  <SelectItem value="PREVIDENCIARIO">PREVIDENCIARIO</SelectItem>
                  <SelectItem value="NEGOCIACAO_DE_PASSIVOS">NEGOCIAÇÃO DE PASSIVOS</SelectItem>
                  <SelectItem value="SIMPLES_NACIONAL">SIMPLES NACIONAL</SelectItem>
                  <SelectItem value="ALIQUOTA_ZERO">ALIQUOTA ZERO</SelectItem>
                  <SelectItem value="APURACAO_ST">APURACAO ST</SelectItem>
                  <SelectItem value="SUBVENCAO">SUBVENÇÃO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenant">Empresas</Label>
              <Select
                onValueChange={value => {
                  const tenant = tenantOptions.find((t: { razaoSocial: string }) => t.razaoSocial === value)
                  setSelectedTenantCNPJ(tenant?.cnpj || '')
                }}
                required
              >
                <SelectTrigger id="tenant">
                  <SelectValue placeholder="Selecione o locatário" />
                </SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((tenant: { razaoSocial: string; cnpj: string }) => (
                    <SelectItem key={tenant.cnpj} value={tenant.razaoSocial}>
                      {tenant.razaoSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              onClick={handleCreateContract}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Criar
            </Button>
          </CardFooter>
        </form>
      </Card>
    )
  } else {
    return (
      <>
        <Card className="w-full max-w-md bg-background-secondary shadow-xl">
          <CardHeader>
            <CardTitle>Enviar Documento</CardTitle>
            <CardDescription>Selecione o documento para o contrato {contractName}.</CardDescription>
          </CardHeader>
          <CardContent>
            <Input id="document" type="file" onChange={handleFileUpload} />
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleCreateAndFinalize}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Criar e Finalizar
            </Button>
          </CardFooter>
        </Card>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Contrato Criado com Sucesso</DialogTitle>
              <DialogDescription>
                O contrato {contractName} foi criado e o documento foi enviado com sucesso.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => {
                  setShowModal(false)
                  setShowForm(true)
                  setContractName('')
                  setContractType('')
                  setFile(null)
                  setSelectedTenantCNPJ('')
                }}
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }
}
